import React from "react";
import PolicyLayout from "../../../components/PolicyLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function MagicEraserPrivacy() {
  return (
    <PolicyLayout
      title="privacy policy"
      appName="Magic Eraser AI: Photo Cleaner"
      date="NOVEMBER 2025"
    >
      {/* Intro */}
      <p>
        This Privacy Policy explains how Magic Eraser AI: Photo Cleaner
        (“we”, “our”, “us”, or “the Application”) collects, uses, stores, and
        protects information when you use our mobile application. By
        downloading or using the Application, you agree to the practices
        described below.
      </p>

      {/* 1. Information We Collect */}
      <div className={s.title}>1) Information We Collect</div>

      <p><strong>Image and Media Data.</strong></p>
      <ul>
        <li>Photos uploaded into the Application are processed on our secure servers to provide background/object removal functionality.</li>
        <li>Processed photos are retained on our servers for up to 30 days and then permanently deleted.</li>
      </ul>

      <p><strong>Automatically Collected Data.</strong></p>
      <ul>
        <li>Device information (model, operating system version, language).</li>
        <li>IP address, crash logs, diagnostic reports.</li>
        <li>Usage data (features accessed, session length, in-app behavior).</li>
      </ul>

      <p><strong>Payment Data.</strong></p>
      <ul>
        <li>Subscription transactions (Weekly Premium – $6.99/week) are handled exclusively by Apple App Store and Google Play.</li>
        <li>We do not store or have access to your payment card details.</li>
      </ul>

      <p><strong>Third-Party Services.</strong></p>
      <p>We use third-party services, which may collect data in accordance with their policies:</p>
      <ul>
        <li>Firebase (analytics and crash reporting)</li>
        <li>Google Analytics (usage statistics)</li>
        <li>RevenueCat (subscription management)</li>
        <li>Photo processing service (AI image editing)</li>
      </ul>

      <p>
        We do not collect names, emails, or registration details.
      </p>

      {/* 2. Legal Basis for Processing (GDPR) */}
      <div className={s.title}>2) Legal Basis for Processing (GDPR)</div>
      <p>We process personal data under the following legal grounds:</p>
      <ul>
        <li><strong>Performance of a contract:</strong> To provide image editing services as requested by you.</li>
        <li><strong>Consent:</strong> Where applicable (e.g., analytics, optional tracking).</li>
        <li><strong>Legitimate interest:</strong> To improve the Application, ensure security, and prevent fraud.</li>
        <li><strong>Legal obligation:</strong> When required to comply with applicable laws.</li>
      </ul>

      {/* 3. How We Use Information */}
      <div className={s.title}>3) How We Use Information</div>
      <ul>
        <li>To provide the Application’s core functionality (AI photo editing).</li>
        <li>To analyze usage and improve performance.</li>
        <li>To resolve technical issues and provide support.</li>
        <li>To manage subscriptions and verify transactions.</li>
        <li>To comply with legal requirements.</li>
      </ul>

      {/* 4. Data Retention */}
      <div className={s.title}>4) Data Retention</div>
      <ul>
        <li>Photos: stored for up to 30 days on our servers, then automatically deleted.</li>
        <li>Analytics and technical logs: retained as long as necessary for analysis and improvement.</li>
        <li>Payment records: managed by App Store/Google Play under their policies.</li>
      </ul>

      {/* 5. Sharing and Disclosure */}
      <div className={s.title}>5) Sharing and Disclosure</div>
      <p>We do not sell or rent your personal data. Data may be shared only:</p>
      <ul>
        <li>With trusted service providers (listed in Section 1.4) under confidentiality agreements.</li>
        <li>To comply with legal obligations (court orders, regulatory requests).</li>
        <li>In the event of a merger, acquisition, or sale of assets, subject to safeguards.</li>
      </ul>

      {/* 6. International Data Transfers */}
      <div className={s.title}>6) International Data Transfers</div>
      <p>
        Your data may be transferred to and processed in countries outside your
        residence. We ensure appropriate safeguards (e.g., GDPR Standard
        Contractual Clauses) for such transfers.
      </p>

      {/* 7. User Rights */}
      <div className={s.title}>7) User Rights</div>

      <p><strong>Under GDPR (EEA users), you have the right to:</strong></p>
      <ul>
        <li>Access and request a copy of your personal data.</li>
        <li>Request correction or deletion of your data.</li>
        <li>Restrict or object to processing.</li>
        <li>Withdraw consent at any time.</li>
        <li>Request data portability.</li>
        <li>File a complaint with your local Data Protection Authority.</li>
      </ul>

      <p><strong>Under CCPA (California users), you have the right to:</strong></p>
      <ul>
        <li>Request disclosure of the categories and specific pieces of personal data collected.</li>
        <li>Request deletion of your data.</li>
        <li>Opt out of the sale of personal data (we do not sell data).</li>
        <li>Not be discriminated against for exercising privacy rights.</li>
      </ul>

      <p>
        To exercise these rights, contact us at{" "}
        <a href="mailto:alexwadek@gmail.com">alexwadek@gmail.com</a>.
      </p>

      {/* 8. Children’s Privacy */}
      <div className={s.title}>8) Children’s Privacy</div>
      <p>
        The Application is not directed to children under 13 (or 16 in certain
        jurisdictions). We do not knowingly collect personal information from
        children. If such data is discovered, we will delete it immediately.
      </p>

      {/* 9. Data Security */}
      <div className={s.title}>9) Data Security</div>
      <p>
        We implement technical and organizational measures, including
        encryption and secure storage, to protect data from unauthorized
        access, disclosure, or loss. However, no system is 100% secure, and
        we cannot guarantee absolute security.
      </p>

      {/* 10. Do Not Track */}
      <div className={s.title}>10) Do Not Track</div>
      <p>
        Our Application does not respond to “Do Not Track” signals, as no
        uniform standard has been established.
      </p>

      {/* 11. Changes to This Policy */}
      <div className={s.title}>11) Changes to This Policy</div>
      <p>
        We may update this Privacy Policy from time to time. Any updates will
        be posted within the Application or on the official distribution
        platforms (App Store/Google Play). Continued use of the Application
        after changes constitutes acceptance of the updated Policy.
      </p>

      {/* 12. Contact Us */}
      <div className={s.title}>12) Contact Us</div>
      <p>For questions or requests regarding this Privacy Policy, please contact:</p>
      <p>Developer: Aleksandra Rakitina</p>
      <p>
        Email:{" "}
        <a href="mailto:alexwadek@gmail.com">alexwadek@gmail.com</a>
      </p>
    </PolicyLayout>
  );
}